import React, { useState, useEffect } from 'react';
import '../styles/ProgramPages.css';
import '../styles/benefits.css';
import '../styles/master.css';
import SEO from '../components/SEO';
import '../styles/participants.css';
import '../styles/learning-experience.css';
import '../styles/pricing.css';
import '../styles/faq.css';
import '../styles/cta-final.css';
import '../styles/PopupForm.css';
import '../styles/features.css';
import '../styles/confirmation-modal.css';
import programsData from '../data/programs.json';
import Footer from '../components/Footer';
import PopupForm from '../components/PopupForm';
import { API_ENDPOINTS } from '../config/constants';
import { generateToken, TOKEN_HEADER } from '../utils/tokenUtils';

function gtag_report_conversion(url) {
  if (window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: 'AW-16782075468',
      event_callback: function () {
        if (url) {
          window.location = url;
        }
      }
    });
    return false;
  }
  return true;
}

function CloudBundleDp900() {
  const data = programsData.cloudBundleDp900;

  const locationParam = new URLSearchParams(window.location.search).get('location') || 'IN';
  const pricingData = data?.pricing?.locations?.[locationParam] || data?.pricing?.locations?.['IN'];
  let pricingCurrency = '', pricingAmount = '', pricingOriginalCurrency = '', pricingOriginalAmount = '';
  
  // Feature highlights for DP-900
  const [featureIndex, setFeatureIndex] = useState(0);
  const featureTiles = [
    {
      icon: '/images/icons/handson.png',
      title: 'Hands-On First',
      desc: 'Deploy Azure data services from day one—no theory overload.'
    },
    {
      icon: '/images/icons/interactive.svg',
      title: 'Bring Your Own Scenario',
      desc: 'Apply to your actual data challenges and business needs.'
    },
    {
      icon: '/images/icons/copilot/certificate.svg',
      title: 'Ready to Apply',
      desc: 'Production-ready data solutions immediately.'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setFeatureIndex(i => (i === featureTiles.length - 1 ? 0 : i + 1));
    }, 5000);
    return () => clearInterval(interval);
  }, [featureTiles.length]);
  if (pricingData) {
    const match = pricingData.current.match(/^(\D+)?(\d+(?:\.\d+)?)/);
    if (match) {
      pricingCurrency = match[1] || '';
      pricingAmount = match[2];
    }
    const origMatch = pricingData.original.match(/^(\D+)?(\d+(?:\.\d+)?)/);
    if (origMatch) {
      pricingOriginalCurrency = origMatch[1] || '';
      pricingOriginalAmount = origMatch[2];
    }
  }

  const [showInlineHeroForm, setShowInlineHeroForm] = useState(true);
  const [inlineName, setInlineName] = useState('');
  const [inlineEmail, setInlineEmail] = useState('');
  const [inlineHeroSubmitting, setInlineHeroSubmitting] = useState(false);
  const [inlineHeroSubmitted, setInlineHeroSubmitted] = useState(false);
  const [inlineHeroError, setInlineHeroError] = useState('');
  const [showScrollUp, setShowScrollUp] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [formType, setFormType] = useState('');
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    message: '',
    title: ''
  });

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY || document.documentElement.scrollTop;
      const pageHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (pageHeight > 0 && scrolled > pageHeight * 0.10) {
        setShowScrollUp(true);
      } else {
        setShowScrollUp(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const openPopup = (type) => {
    setFormType(type);
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
  };

  const showConfirmation = (type) => {
    const messages = {
      enroll: {
        title: "Enrollment Successful!",
        message: "Thank you for enrolling in Azure Data Fundamentals DP-900. We'll send you hands-on lab access and Azure data services setup guide via email."
      }
    };
    
    setConfirmationModal({
      isOpen: true,
      title: messages[type].title,
      message: messages[type].message
    });

    setTimeout(() => {
      setConfirmationModal(prev => ({ ...prev, isOpen: false }));
    }, 3000);
  };

  const closeConfirmation = () => {
    setConfirmationModal(prev => ({ ...prev, isOpen: false }));
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      question: "What is the DP-900 certification?",
      answer: "Microsoft Azure Data Fundamentals (DP-900) validates your foundational knowledge of core data concepts (relational, non-relational, analytics) and Azure data services. It's a beginner-level certification ideal for starting your data career."
    },
    {
      question: "How is this different from other courses?",
      answer: "We follow Lead With Tech principles: Hands-On First (deploy data services from day one), Bring Your Own Scenario (apply to your data challenges), and Ready to Apply (build production solutions immediately)."
    },
    {
      question: "Do I need prior data experience?",
      answer: "No prior data experience required. Familiarity with basic IT concepts is helpful. We start with core data concepts and progress to hands-on Azure data service deployment."
    },
    {
      question: "How long does it take to complete?",
      answer: "Most students complete hands-on practice in 4-6 weeks. The exam itself is 45 minutes with 40-60 questions. You can go at your own pace with lifetime access."
    },
    {
      question: "What's included in the course?",
      answer: "Hands-on labs for all 4 exam domains (Core Data Concepts, Relational Data, Non-Relational Data, Analytics), practice assessments, exam sandbox access, and official study guide alignment."
    },
    {
      question: "Can I bring my own data scenarios?",
      answer: "Absolutely! We encourage using your own business data challenges to practice. That's our 'Bring Your Own Scenario' principle in action."
    }
  ];

  return (
    <div className="dp900-page" style={{ position: 'relative' }}>
      <SEO pageName="cloudBundleDp900" />
      <section className="hero-section" style={{ position: 'relative', backgroundImage: 'url(/images/background.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', width: '100%' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.8)', zIndex: 1 }}></div>
        <div className="hero-content responsive-hero-content" style={{ display: 'flex', alignItems: 'center', gap: '2.5rem', position: 'relative', zIndex: 2 }}>
          <div className="hero-text" style={{ flex: 1 }}>
            <h1>{data.hero.title}</h1>
            <p className="hero-subtitle">{data.hero.subtitle}</p>
            {showInlineHeroForm && (
              <div className="inline-hero-lead-form" style={{ marginTop: 18, borderRadius: 10, boxShadow: '0 2px 12px rgba(44,62,80,0.10)', padding: 18, maxWidth: 280 }}>
                <form
                  onSubmit={async e => {
                    e.preventDefault();
                    if (!inlineName.trim() || !inlineEmail.trim()) {
                      setInlineHeroError('Please enter your name and email.');
                      return;
                    }
                    setInlineHeroError('');
                    setInlineHeroSubmitting(true);
                    try {
                      let token = localStorage.getItem('token');
                      if (!token) {
                        token = generateToken();
                        localStorage.setItem('token', token);
                      }
                      const headers = {
                        'Content-Type': 'application/json',
                        [TOKEN_HEADER]: token
                      };
                      const response = await fetch(API_ENDPOINTS.LEADS_SUBMIT, {
                        method: 'POST',
                        headers,
                        body: JSON.stringify({
                          name: inlineName,
                          email: inlineEmail,
                          source: 'dp900_hero',
                          interestedIn: 'dp900_certification'
                        })
                      });
                      if (!response.ok) throw new Error('Failed to submit');
                      setInlineHeroSubmitted(true);
                      setTimeout(() => {
                        setShowInlineHeroForm(false);
                        setInlineHeroSubmitted(false);
                        setInlineName('');
                        setInlineEmail('');
                      }, 2500);
                    } catch (e) {
                      setInlineHeroError('Failed to submit. Please try again.');
                    } finally {
                      setInlineHeroSubmitting(false);
                    }
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div className="inline-hero-form-row">
                      <input
                        type="text"
                        placeholder="Name"
                        value={inlineName}
                        onChange={e => setInlineName(e.target.value)}
                        disabled={inlineHeroSubmitting}
                        style={{ flex: 1, padding: 8, borderRadius: 5, border: '1px solid #ccc', minWidth: 120 }}
                      />
                      <input
                        type="email"
                        placeholder="Email"
                        value={inlineEmail}
                        onChange={e => setInlineEmail(e.target.value)}
                        disabled={inlineHeroSubmitting}
                        style={{ flex: 1, padding: 8, borderRadius: 5, border: '1px solid #ccc', minWidth: 120 }}
                      />
                      <button
                        type="submit"
                        disabled={inlineHeroSubmitting}
                        style={{ background: '#DA291C', color: '#fff', border: 'none', borderRadius: 5, padding: '8px 18px', fontWeight: 600, fontSize: '1rem', cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >
                        {inlineHeroSubmitting ? 'Submitting...' : 'Enroll Now'}
                      </button>
                    </div>
                    {inlineHeroError && <div style={{ color: '#DA291C', fontSize: '0.95rem' }}>{inlineHeroError}</div>}
                    {inlineHeroSubmitted && <div style={{ color: 'green', fontWeight: 600 }}>Thank you! We'll be in touch soon.</div>}
                  </div>
                </form>
              </div>
            )}
            <p className="stats-text" dangerouslySetInnerHTML={{ __html: data.hero.statsText }} />
          </div>
          <div className="hero-image responsive-hero-image" style={{ flex: '0 0 340px', textAlign: 'right' }}>
            <img 
              src={data.image} 
              alt="Azure Data DP-900 Certification"
              className="rounded-image"
              style={{ maxWidth: '340px', width: '100%', height: 'auto', borderRadius: '18px', boxShadow: '0 4px 24px rgba(44,62,80,0.10)' }}
            />
          </div>
        </div>
      </section>

      <section style={{ background: '#fff', padding: '48px 0 32px 0' }}>
        <h2 style={{ textAlign: 'center', fontWeight: 700, fontSize: '2.2rem', marginBottom: '0.5rem', color: '#222' }}>
          Master Azure Data Fundamentals — <span style={{ color: '#DA291C' }}>The Lead With Tech Way</span>
        </h2>
        <p style={{ textAlign: 'center', fontSize: '1.15rem', color: '#444', marginBottom: '2.5rem' }}>
          Hands-On First · Bring Your Own Scenario · Ready to Apply
        </p>
        <div className="dp900-feature-slider" style={{ maxWidth: 400, margin: '0 auto' }}>
          <div
            style={{
              background: '#fff',
              borderRadius: '18px',
              boxShadow: '0 2px 12px rgba(44,62,80,0.08)',
              padding: '32px',
              minWidth: '220px',
              maxWidth: '360px',
              margin: '0 auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              transition: 'all 0.3s',
            }}
          >
            <img src={featureTiles[featureIndex].icon} alt={featureTiles[featureIndex].title} style={{ width: '64px', height: '64px', marginBottom: '18px', display: 'block' }} />
            <h3 style={{ fontWeight: 700, fontSize: '1.25rem', marginBottom: '12px', color: '#222' }}>{featureTiles[featureIndex].title}</h3>
            <p style={{ color: '#444', fontSize: '1rem' }}>{featureTiles[featureIndex].desc}</p>
          </div>
        </div>
      </section>

      <section className="master-section">
        <div className="container">
          <h2>{data.curriculum?.title || '4 Core Domains'} — <span className="highlight">Hands-On Mastery</span></h2>
          <p className="subtitle">{data.curriculum?.subtitle || 'Aligned with official DP-900 exam • Build real Azure data solutions'}</p>
          
          <div className="master-grid">
            {data.curriculum?.domains?.map((domain, index) => (
              <div key={index} className="master-card">
                <div className="master-icon">
                  <img src={domain.icon} alt={domain.title} />
                </div>
                <div className="master-content">
                  <h3>{domain.title}</h3>
                  <p>{domain.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pricing-section">
        <div className="container">
          <h2>Course <span className="highlight">Pricing</span></h2>
          <div className="pricing-card">
            <div className="pricing-header">
              <div className="pricing-amount">
                <span className="original-price">{pricingOriginalCurrency}{pricingOriginalAmount}</span>
                <span className="current-price">{pricingCurrency}{pricingAmount}</span>
              </div>
              <p className="pricing-save">(Save 50%)</p>
            </div>
            <div className="pricing-features">
              <div>✓ Hands-on labs for all 4 domains</div>
              <div>✓ Real Azure data service deployment</div>
              <div>✓ Practice assessments + exam sandbox</div>
              <div>✓ Your own data scenario guidance</div>
              <div>✓ Production-ready data skills</div>
              <div>✓ Official study guide alignment</div>
            </div>
            <button 
              onClick={() => { gtag_report_conversion(); openPopup('enroll'); }} 
              className="enroll-button"
            >
              Enroll Now
            </button>
            <p className="pricing-note">Hands-on practice. Deploy real data solutions. Exam-ready in 45 days.</p>
          </div>
        </div>
      </section>

      <section className="faq-section">
        <div className="container">
          <h2>Frequently Asked <span className="highlight">Questions</span></h2>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <button 
                  className="faq-question" 
                  onClick={() => toggleFaq(index)}
                >
                  {faq.question}
                  <span className={`faq-toggle ${openFaq === index ? 'open' : ''}`}>
                    +
                  </span>
                </button>
                <div className={`faq-answer ${openFaq === index ? 'open' : ''}`}>
                  {faq.answer}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="cta-final">
        <div className="container">
          <h2>Ready to Master Azure Data Fundamentals?</h2>
          <p>Build real data solutions today. Apply to your projects tomorrow. Get certified next month.</p>
          <div className="cta-buttons">
            <button 
              onClick={() => { gtag_report_conversion(); openPopup('enroll'); }} 
              className="cta-button cta-button-primary"
            >
              Enroll Now
            </button>
          </div>
        </div>
      </section>

      <PopupForm 
        isOpen={isPopupOpen} 
        onClose={closePopup} 
        formType={formType}
        onSubmit={async (formData) => {
          try {
            await fetch(API_ENDPOINTS.LEADS_SUBMIT, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                ...formData,
                source: 'dp900_page',
                interestedIn: 'dp900_certification'
              })
            });
            closePopup();
            showConfirmation('enroll');
          } catch (error) {
            console.error('Error submitting form:', error);
            alert('Failed to submit form. Please try again.');
          }
        }}
      />

      {showScrollUp && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{
            position: 'fixed',
            right: '32px',
            bottom: '40px',
            zIndex: 1300,
            background: '#fff',
            border: '2px solid #DA291C',
            borderRadius: '50%',
            width: '54px',
            height: '54px',
            boxShadow: '0 2px 12px rgba(44,62,80,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'opacity 0.2s',
            opacity: 0.95
          }}
          aria-label="Scroll to top"
        >
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="16" cy="16" r="16" fill="#fff" />
            <path d="M10 18l6-6 6 6" stroke="#DA291C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </svg>
        </button>
      )}

      {confirmationModal.isOpen && (
        <div className="confirmation-modal">
          <div className="confirmation-content">
            <button className="close-button" onClick={closeConfirmation}>&times;</button>
            <h3>{confirmationModal.title}</h3>
            <p>{confirmationModal.message}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default CloudBundleDp900;
